import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Headers, Http, Request, RequestOptions, Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';

import { Communication } from './communication.model';
import { CommunicationsType, CommunicationOutcomes, CommunicationActions } from './communication-types.enum';
import { EnumTextValuesPipe } from '../pipes/enum-keys-text.pipe';
import { TeamMember } from '../../features/team/team.model';
import { Contact } from '../../features/contacts/contact.model';
import { ContactService } from '../../features/contacts/contact.service';
import { TeamService } from '../../features/team/team.service';
import { ToastrService } from 'ngx-toastr';
import { Purchase } from '../../features/projects/purchase.model';
import { EnumStringsPipe } from '../pipes/enum-strings.pipe';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';

@Injectable()
export class CommunicationService implements OnDestroy {

  commsOutcomes = CommunicationOutcomes;
  commsOutcomeOptions: any;

  commsType = CommunicationsType;
  commsTypeOptions: any;

  commsActions = CommunicationActions;
  commsActionsOptions: any;

  keepSubscriptionsAlive = true;
  teamMembers: TeamMember[];
  contacts: Contact[];

  // dropdown settings
  dropdownSettings = {
    singleSelection: false,
    text: 'Select',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    enableSearchFilter: true,
    classes: 'myclass custom-class'
  };


  dtSettings = {
    bigBanner: true,
    closeOnSelect: true,
    timePicker: true,
    format: 'dd/MM/yyyy hh:mm a',
    defaultOpen: false
  };

  // employee drop down
  employeesDropdownList = [];

  // contact drop down
  contactsDropdownList = [];


  constructor(
    private http: HttpClient,
    private contactService: ContactService,
    private teamService: TeamService,
    private toastr: ToastrService,
    private enumTextValuesPipe: EnumTextValuesPipe,
    private enumStringsPipe: EnumStringsPipe,

  ) { }

  ngOnDestroy() { this.keepSubscriptionsAlive = false; }

  setCommsTypeOptions() {
    this.commsTypeOptions = this.enumTextValuesPipe.transform(this.commsType, ['']);
    this.commsOutcomeOptions = this.enumStringsPipe.transform(this.commsOutcomes, ['']);
    this.commsActionsOptions = this.enumTextValuesPipe.transform(this.commsActions, ['']);
  }

  getTeamMembersList() {

    this.teamService.getTeamMembers()
      .takeWhile(() => this.keepSubscriptionsAlive)
      .subscribe(
        res => {
          this.teamMembers = <TeamMember[]>res;
          this.createEmployeesDropdownList();
        },
        error => console.log(error),
    );

  }

  getContactsList(contacts: Contact[]) {

    // if its just a list of ids, we need to get them
    if (contacts.length>0 && typeof contacts[0] === 'string') {

      const contactIds = contacts.join();

      // get them from their ids
      this.contactService.getContactsFromIds(contactIds)
        .takeWhile(() => this.keepSubscriptionsAlive)
        .subscribe(
          res => {
            this.contacts = <Contact[]>res;
            this.createContactsDropdownList();
          },
          error => console.log(error),
      );

    } else {

      // else, we can just do the contacts drop down
      this.contacts = contacts;
      this.createContactsDropdownList();
    }


  }

  createEmployeesDropdownList() {
    this.employeesDropdownList = this.teamMembers.map(tm => ({ 'id': tm._id, 'itemName': tm.firstName + ' ' + tm.lastName, 'teamMember': tm }));

  }
  createContactsDropdownList() {
    // console.log('list of contacts', this.contacts);
    this.contactsDropdownList = this.contacts.map(c => ({ 'id': c._id, 'itemName': c.firstName + ' ' + c.lastName, 'contact': c }));
  }




  searchCommunications(searchString: string): Observable<Communication[]> {
    // see https://github.com/surfer77/mongoose-string-query for full API!
    return this.http.get<Communication[]>('/api/communications/search?' + searchString).map((communications: Communication[]) => {
      return communications;
    }).catch(this.handleError);

  }

  // Defines routes for communication data retrieval
  getCommunications(): Observable<Communication[]> {
    return this.http.get<Communication[]>('/api/communications').map((communications: Communication[]) => {
      return communications;
    }).catch(this.handleError);
  }

  getCommunicationsFromIds(contactIds: string): Observable<Communication[]> {
    return this.http.get<Communication[]>(`/api/communications/listByIds/${contactIds}`);
    // ).get(requireAuth, contactCtrl.get);
  }

  countCommunications(): Observable<number> {
    return this.http.get<number>('/api/communications/count');
  }

  getContactsCreatedFromTo(from: string, to: string): Observable<Contact[]> {
    return this.http.get<Contact[]>(`/api/contacts/byDate/${from}/${to}`);
  }

  addMassCommunication(communication: Communication, purchases: Purchase[]): Observable<string> {
    return this.http.post('/api/communication/mass', { communication: communication, purchases: purchases }, { responseType: 'text' });
  }

  addCommunication(communication: Communication): Observable<Communication> {
    return this.http.post<Communication>('/api/communication', communication);
  }

  getCommunicationFromId(id: string): Observable<Communication> {
    return this.http.get<Communication>(`/api/communication/${id}`);
  }

  getCommunication(communication: Communication): Observable<Communication> {
    return this.http.get<Communication>(`/api/communication/${communication._id}`);
  }

  getPurchaseCommunicationsByUserId(userId: string, limit: number = 100, from: string = moment().add('-1', 'month').toISOString(), to: string = moment().endOf('day').toISOString()): Observable<Communication[]> {
    return this.http.get<Communication[]>(`/api/communications/getPurchaseCommunicationsByUserId/${userId}/${from}/${to}/${limit}`);
  }

  getCommunicationTimelinesById(userId: string, from: string = moment().add('-20', 'year').toISOString(), to: string = moment().endOf('day').toISOString()): Observable<any> {
    return this.http.get<any>(`/api/communications/getCommunicationTimelinesById/${userId}/${from}/${to}`);
  }

  getUserCommunicationSummary(userId: string): Observable<any> {
    return this.http.get<any>(`/api/communications/getCommunicationsSummaryById/${userId}`);
  }

  getCallerCommunicationsReport(userId: string, from: string = moment().add('-1', 'day').startOf('day').toISOString(), to: string = moment().endOf('day').toISOString()): Observable<Blob> {
    return this.http.get(`/api/communications/getCallerCommunicationsReport/${userId}/${from}/${to}`, { responseType: 'blob' }).catch(this.handleError);
  }

  editCommunication(communication: Communication): Observable<string> {
    return this.http.put(`/api/communication/${communication._id}`, communication, { responseType: 'text' });
  }

  deleteCommunication(communication: Communication): Observable<string> {
    return this.http.delete(`/api/communication/${communication._id}`, { responseType: 'text' });
  }


  private handleError(error: Response) {
    return Observable.throw(error.statusText);
  }

  downloadCallerCommunicationsReport(tm: TeamMember, from: string = moment().add('-1', 'day').format('DD/MM/YYYY'), to: string = moment().add('-1', 'day').startOf('day').format('DD/MM/YYYY')) {

    this.getCallerCommunicationsReport(tm._id, from, to)
      .takeWhile(() => this.keepSubscriptionsAlive)
      .subscribe(res => {
        console.log(res);
        FileSaver.saveAs(res, tm.fullName + ' - Caller List.docx');
      },
        error => {
          this.toastr.warning('An error occured and the file could not be downloaded.');
          console.log(error);
        }
      );

  }

}



// WEBPACK FOOTER //
// ./client/app/shared/communications/communication.service.ts